import {
  OreStack,
  OreTable,
  OreTableBody,
  OreTableCell,
  OreTableContainer,
  OreTableHead,
  OreTableRow,
  OreText,
  OrePagination,
  OreMessageBar
} from '@edenredespana/oreneta'
import { useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { storesTranslation } from '../storesTranslation'
import { useNavigate } from 'react-router-dom'
import { ExcelLayout } from '@/Store/ui/templates/ExcelLayout'
import { navigationRoutes } from '@/Shared/ui/routers'
import { TableHeadersModel } from '@/Shared/domain/models/TableHeadersModel'
import { tablePagination } from '@/Shared/ui/sharedTranslation'
import { EllipsisTextTooltip } from '@/Shared/ui/ellipsisTextTooltip'
import { Steps, TPVStatusPill } from '@/Store/ui/atoms'
import { useExcelUpload } from '@/Store/ui/context'

const ROWS_PER_PAGE = 8

export const ExcelConfirmation = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState<number>(1)
  const [errors, setErrors] = useState<boolean>(true)
  const {
    setStep,
    insertResultsResponse,
    downloadInsertErrorsFile
  } = useExcelUpload()
  const insertResults = insertResultsResponse?.insertResults ?? []

  const headers: TableHeadersModel[] = [
    {
      key: 'merchantId',
      label: storesTranslation.tpvs.list.table.affiliateCode,
      size: 'auto',
    },
    { key: 'merchantName', label: storesTranslation.tpvs.list.table.local, size: 'auto' },
    {
      key: 'fullAddress',
      label: storesTranslation.tpvs.list.table.address,
      size: 'quarter',
    },
    {
      key: 'terMid',
      label: storesTranslation.tpvs.list.table.commerceCode,
      size: 'quarter',
    },
    {
      key: 'isSuccess',
      label: storesTranslation.tpvs.list.table.status,
      size: 'auto',
    },
  ]

  useEffect(() => {
    setErrors(insertResults.some(item => !item.isSuccess))
  }, [insertResults])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])

  return (
    <ExcelLayout
      onContinue={() => navigate(navigationRoutes.storeTPV)}
      OnContinueText={t(storesTranslation.excelTPVConfirmation.seeTerminalsButton)}>
      <OreStack space="large" placeContent="stretch">
        {errors && (
          <OreMessageBar>
            <OreText tone="error-600">
              <Trans
                components={[
                  <button
                    key="download-insert-errors"
                    type="button"
                    style={{ color: 'inherit', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold', font: 'inherit', background: 'none', border: 0, padding: 0 }}
                    onClick={downloadInsertErrorsFile}
                  />,
                  <button
                    key="return-to-upload"
                    type="button"
                    style={{ color: 'inherit', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold', font: 'inherit', background: 'none', border: 0, padding: 0 }}
                    onClick={() => setStep(Steps.Upload)}
                  />]}
              >
                {t(storesTranslation.excelTPVConfirmation.errorMessage)}
              </Trans>
            </OreText>
          </OreMessageBar>
        )}
        <OreTableContainer>
          <OreTable>
            <OreTableHead valign="middle">
              <OreTableRow hover={false}>
                {headers.map(header => (
                  <OreTableCell as="th" key={header.key} size={header.size}>
                    <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                      {header.label && t(header.label)}
                    </span>
                  </OreTableCell>
                ))}
              </OreTableRow>
            </OreTableHead>
            <OreTableBody valign="top">
              {insertResults.length ? (
                insertResults.map(item => (
                  <OreTableRow key={item.terMid}>
                    <OreTableCell>
                      <OreText size="text-sm" tone="neutral-600">
                        {item.merchantId}
                      </OreText>
                    </OreTableCell>
                    <OreTableCell>
                      <EllipsisTextTooltip text={item.merchantName} />
                    </OreTableCell>
                    <OreTableCell>
                      <EllipsisTextTooltip text={item.fullAddress} />
                    </OreTableCell>
                    <OreTableCell>
                      <OreText size="text-sm" tone="neutral-600">
                        {item.terMid}
                      </OreText>
                    </OreTableCell>
                    <OreTableCell>
                      <TPVStatusPill tpv={item} />
                    </OreTableCell>
                  </OreTableRow>
                ))
              ) : (
                <OreTableRow valign="middle">
                  <OreTableCell colSpan={headers.length}>
                    <OreText size="text-md" data-testid="noTpvs">
                      {insertResults.length ?
                        t(storesTranslation.tpvs.list.noTpvs) :
                        t(storesTranslation.tpvs.list.noTpvsTableText)}
                    </OreText>
                  </OreTableCell>
                </OreTableRow>
              )}
            </OreTableBody>
          </OreTable>
          {insertResults.length > 0 ? (
            <OrePagination
              displayPageButtons
              count={insertResults.length}
              labelNextPage={t(tablePagination.nextPageLabel)}
              labelPreviousPage={t(tablePagination.previousPageLabel)}
              onPageChange={setPage}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
              text={t(tablePagination.pageOf)}
              legend={t(tablePagination.rowsShowing)}
            />
          ) : null}
        </OreTableContainer>
      </OreStack>
    </ExcelLayout>
  )
}
